import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Search, Plus, Trash2, Clock, User, X, Edit, Loader2, Save } from 'lucide-react';
import { BullhornNoteEditor } from './BullhornNoteEditor';
import { formatVoorgesteldCandidateNoteForDisplay } from '../utils/portalProposalThread';

interface CandidateNote {
    id: string;
    candidate_id: string;
    author_name: string;
    content: string;
    created_at: string;
    updated_at: string;
}

interface CandidateNotesProps {
    candidateId: string;
    currentUser: any;
    systemNotes?: any[];
}

export const CandidateNotes: React.FC<CandidateNotesProps> = ({ candidateId, currentUser, systemNotes = [] }) => {
    const [notes, setNotes] = useState<CandidateNote[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Editor State
    const [isEditing, setIsEditing] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [noteContent, setNoteContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!candidateId) return;
        fetchNotes();
    }, [candidateId]);

    const fetchNotes = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('candidate_notes')
                .select('*')
                .eq('candidate_id', candidateId)
                .order('created_at', { ascending: false });
                
            if (error) {
                // Ignore if table doesn't exist yet (user didn't run SQL)
                console.warn(error);
                setNotes([]);
            } else if (data) {
                setNotes(data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!noteContent.trim() || !currentUser?.id) return;
        if (String(currentUser.id) === 'current-user') {
            alert('Gebruiker niet geladen. Vernieuw de pagina en probeer opnieuw.');
            return;
        }
        setIsSaving(true);
        
        try {
            if (editingNoteId) {
                // Update
                const { error } = await supabase
                    .from('candidate_notes')
                    .update({ content: noteContent, updated_at: new Date().toISOString() })
                    .eq('id', editingNoteId);
                if (error) throw error;
            } else {
                const authorName =
                    currentUser.name ||
                    currentUser.profile?.name ||
                    currentUser.user_metadata?.full_name ||
                    currentUser.email ||
                    'Onbekend';
                // Insert
                const { error } = await supabase
                    .from('candidate_notes')
                    .insert({
                        candidate_id: candidateId,
                        author_id: currentUser.id,
                        author_name: authorName,
                        content: noteContent
                    });
                if (error) throw error;
            }
            
            await fetchNotes();
            closeEditor();
        } catch (e: any) {
            alert(`Fout bij opslaan notitie: ${e.message || 'Onbekend rapport'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Weet je zeker dat je deze notitie wilt verwijderen?")) return;
        try {
            const { error } = await supabase.from('candidate_notes').delete().eq('id', id);
            if (error) throw error;
            setNotes(notes.filter(n => n.id !== id));
        } catch (e) {
            console.error(e);
            alert("Kon notitie niet verwijderen.");
        }
    };

    const openEditor = (note?: CandidateNote) => {
        if (note) {
            setEditingNoteId(note.id);
            setNoteContent(note.content);
        } else {
            setEditingNoteId(null);
            setNoteContent('');
        }
        setIsEditing(true);
    };

    const closeEditor = () => {
        setIsEditing(false);
        setEditingNoteId(null);
        setNoteContent('');
    };

    const safeFormatDate = (dateString: string) => {
        try { return new Date(dateString).toLocaleString('nl-NL'); }
        catch { return String(dateString); }
    };

    // Merge systemNotes (from candidate_communications) and candidate_notes.
    // Prefix ids so rows never collide with candidate_notes UUIDs (fixes duplicate React keys).
    const mergedNotes = [
        ...notes,
        ...systemNotes.map((n) => ({
            id: `comm:${n.id}`,
            candidate_id: candidateId,
            author_name: 'Systeem / Froukje',
            content: n.content,
            created_at: n.created_at,
            updated_at: n.updated_at,
            isSystem: true as const,
        })),
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const filteredNotes = mergedNotes.filter(n => (n.content?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (n.author_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()));

    return (
        <div className="w-full flex flex-col h-full bg-white relative">
            <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 gap-4">
                <h2 className="text-[16px] font-black text-slate-800 flex items-center gap-2">
                    Notities <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">{mergedNotes.length}</span>
                </h2>
                
                {!isEditing && (
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input
                                type="text"
                                placeholder="Zoek notities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                            />
                        </div>
                        <button 
                            onClick={() => openEditor()} 
                            className="flex items-center gap-2 bg-[#e51b23] text-white px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest hover:bg-[#B71C1C] transition-colors"
                        >
                            <Plus size={14} /> Nieuwe Notitie
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="flex-1 w-full bg-white relative animate-fadeIn overflow-y-auto">
                    <BullhornNoteEditor
                        mode="candidate"
                        targetId={candidateId}
                        currentUser={currentUser}
                        onClose={closeEditor}
                        onSaved={() => { fetchNotes(); closeEditor(); }}
                        initialData={editingNoteId ? {
                            id: editingNoteId,
                            content: noteContent
                            // Assuming we stored action_type etc if they existed. For now just content.
                        } : undefined}
                    />
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto p-0">
                    {isLoading ? (
                        <div className="flex justify-center p-12"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
                    ) : filteredNotes.length === 0 ? (
                        <div className="p-12 text-center flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4"><Edit size={24} /></div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Geen Notities Gevonden</h3>
                            <p className="text-xs text-slate-500 font-medium mb-6">Voeg notities toe om belangrijke informatie over deze kandidaat bij te houden.</p>
                            <button onClick={() => openEditor()} className="flex items-center gap-2 bg-white border border-[#e51b23] text-[#e51b23] px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors">Start met typen</button>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                                <tr>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Datum</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Auteur</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 w-1/2">Notitie</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Acties</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredNotes.map((note) => (
                                    <tr key={note.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="py-4 px-6 align-top">
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                                                <Clock size={12} className="text-slate-300"/> {safeFormatDate(note.created_at)}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 align-top">
                                            <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                                                <div className="w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-[9px] uppercase">{(note.author_name || '?').charAt(0)}</div>
                                                {note.author_name || 'Onbekend'}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 align-top">
                                            <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                                                {formatVoorgesteldCandidateNoteForDisplay(note.content)}
                                            </p>
                                            {((note as any).action_type || (note as any).next_action_type) && (
                                                <div className="flex gap-2 mt-2">
                                                    {(note as any).action_type && (note as any).action_type !== 'Geen' && <span className="bg-blue-50 text-blue-600 text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-widest">{(note as any).action_type}</span>}
                                                    {(note as any).next_action_type && (note as any).next_action_type !== 'Geen' && <span className="bg-purple-50 text-purple-600 text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-widest">Opvolging: {(note as any).next_action_type}</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-4 px-6 align-top text-right">
                                            {!(note as any).isSystem && (
                                                <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => openEditor(note)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Bewerken"><Edit size={14}/></button>
                                                    <button onClick={() => handleDelete(note.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Verwijderen"><Trash2 size={14}/></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};
